import {
    useIntegrityProjects,
    useIntegritySamples,
    useIntegrityAcquisitions,
    useDeleteImagesFromDatabase,
    useDeleteImagesFromS3,
    useMoveImagesToSample,
    useUpdateImageAcquisitionDate,
    type IntegrityAcquisitionFile,
} from '@/services/image.service';
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PencilIcon,
    CheckIcon,
    XIcon,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';

export default function BackOfficeImageList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedProjectId = searchParams.get('project');
    const selectedSampleId = searchParams.get('sample');

    const setProject = (projectId: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (projectId) next.set('project', projectId);
        else next.delete('project');
        next.delete('sample');
        setSearchParams(next);
    };
    const setSample = (sampleId: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (sampleId) next.set('sample', sampleId);
        else next.delete('sample');
        setSearchParams(next);
    };

    if (selectedProjectId && selectedSampleId) {
        return (
            <AcquisitionsView
                projectId={selectedProjectId}
                sampleId={selectedSampleId}
                onBack={() => setSample(null)}
            />
        );
    }
    if (selectedProjectId) {
        return (
            <SamplesView
                projectId={selectedProjectId}
                onSelectSample={setSample}
                onBack={() => setProject(null)}
            />
        );
    }
    return <ProjectsView onSelectProject={setProject} />;
}

function ProjectsView({
    onSelectProject,
}: {
    onSelectProject: (id: string) => void;
}) {
    const { data: projects, isLoading, error } = useIntegrityProjects();

    if (isLoading)
        return (
            <BackofficePage title="Images" description="Loading projects…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Images" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    return (
        <BackofficePage
            title="Images"
            description="Browse projects, samples and acquisitions."
        >
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Project name</TH>
                            <TH align="right"> </TH>
                        </tr>
                    </thead>
                    <tbody>
                        {projects?.map((project) => (
                            <tr
                                key={project.projectId}
                                onClick={() =>
                                    onSelectProject(project.projectId)
                                }
                                className="border-b border-[#1e242a] last:border-b-0 hover:bg-[#1a2126] transition cursor-pointer"
                            >
                                <TD>{project.projectName}</TD>
                                <TD align="right">
                                    <ChevronRightIcon className="h-4 w-4 text-[#6b7480] inline" />
                                </TD>
                            </tr>
                        ))}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}

function SamplesView({
    projectId,
    onSelectSample,
    onBack,
}: {
    projectId: string;
    onSelectSample: (id: string) => void;
    onBack: () => void;
}) {
    const { data: projects } = useIntegrityProjects();
    const projectName = useMemo(
        () =>
            projects?.find((p) => p.projectId === projectId)?.projectName ??
            projectId,
        [projects, projectId],
    );
    const { data: samples, isLoading, error } = useIntegritySamples(projectId);

    return (
        <BackofficePage
            title="Images"
            description={`${projectName} — samples`}
            actions={
                <Button
                    variant="ghost"
                    onClick={onBack}
                    className="text-[#a8b0bb] hover:bg-[#1e242a]"
                >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Back to projects
                </Button>
            }
        >
            {isLoading ? (
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            ) : error ? (
                <div className="text-red-400 text-sm">{error.toString()}</div>
            ) : (
                <DataCard>
                    <DataTable>
                        <thead className="bg-[#0f141a]">
                            <tr>
                                <TH>Sample name</TH>
                                <TH>Scanner</TH>
                                <TH align="right"> </TH>
                            </tr>
                        </thead>
                        <tbody>
                            {samples?.map((sample) => (
                                <tr
                                    key={sample.sampleId}
                                    onClick={() =>
                                        onSelectSample(sample.sampleId)
                                    }
                                    className="border-b border-[#1e242a] last:border-b-0 hover:bg-[#1a2126] transition cursor-pointer"
                                >
                                    <TD>{sample.sampleName}</TD>
                                    <TD mono>
                                        {sample.scannerSerialNumber ?? (
                                            <span className="text-[#6b7480]">
                                                —
                                            </span>
                                        )}
                                    </TD>
                                    <TD align="right">
                                        <ChevronRightIcon className="h-4 w-4 text-[#6b7480] inline" />
                                    </TD>
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                </DataCard>
            )}
        </BackofficePage>
    );
}

function AcquisitionsView({
    projectId,
    sampleId,
    onBack,
}: {
    projectId: string;
    sampleId: string;
    onBack: () => void;
}) {
    const { data: projects } = useIntegrityProjects();
    const { data: samples } = useIntegritySamples(projectId);
    const projectName = useMemo(
        () =>
            projects?.find((p) => p.projectId === projectId)?.projectName ??
            projectId,
        [projects, projectId],
    );
    const sample = samples?.find((s) => s.sampleId === sampleId);
    const sampleName = sample?.sampleName ?? sampleId;
    const scannerSerial = sample?.scannerSerialNumber;

    const {
        data: acquisitions,
        isLoading,
        error,
    } = useIntegrityAcquisitions(projectId, sampleId);
    const { mutateAsync: deleteFromDb } = useDeleteImagesFromDatabase();
    const { mutateAsync: deleteFromS3 } = useDeleteImagesFromS3();
    const { mutateAsync: moveImages } = useMoveImagesToSample();
    const { mutateAsync: updateAcquisitionDate } =
        useUpdateImageAcquisitionDate();

    // One selection, of acquisitions (by their jp2 key); each action takes
    // from it what it can act on.
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [moving, setMoving] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => setSelected(new Set()), [projectId, sampleId]);

    const all = useMemo(
        () => (acquisitions ?? []).map((a) => a.jp2Name),
        [acquisitions],
    );
    const picked = (acquisitions ?? []).filter((a) =>
        selected.has(a.jp2Name),
    );
    const movable = picked.filter((a) => a.inDatabase && a.imageId);
    const inDb = picked.filter((a) => a.inDatabase);
    const inS3 = picked.filter((a) => a.filesInS3 > 0);

    const emptyCount = useMemo(
        () =>
            (acquisitions ?? []).filter(
                (a) => isEmptyFile(a.jp2) && isEmptyFile(a.thumb),
            ).length,
        [acquisitions],
    );

    const allSelected = all.length > 0 && selected.size === all.length;
    const someSelected = selected.size > 0 && !allSelected;

    const toggle = (name: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });

    const reportFailures = (
        what: string,
        done: number,
        failures: { name: string; error: string }[],
    ) => {
        if (failures.length === 0) return;
        window.alert(
            `${what} ${done}/${done + failures.length}. ` +
                `Failures:\n${failures.map((f) => `${f.name}: ${f.error}`).join('\n')}`,
        );
    };

    /** S3 first, then the database: a row is never left pointing at files
     *  that are gone because the second half failed silently. */
    const runDelete = async (fromDb: boolean, fromS3: boolean) => {
        const dbNames = fromDb ? inDb.map((a) => a.jp2Name) : [];
        const s3Names = fromS3 ? inS3.map((a) => a.jp2Name) : [];
        if (dbNames.length === 0 && s3Names.length === 0) return;
        const where =
            fromDb && fromS3
                ? 'from the database and S3'
                : fromDb
                  ? 'from the database only (the S3 files stay)'
                  : 'from S3 only (the database rows stay)';
        if (
            !window.confirm(
                `Delete ${Math.max(dbNames.length, s3Names.length)} acquisition(s) ${where}?`,
            )
        )
            return;
        setBusy(true);
        try {
            if (s3Names.length > 0) {
                const r = await deleteFromS3(s3Names);
                reportFailures('Deleted from S3', r.deleted, r.failures);
            }
            if (dbNames.length > 0) {
                const r = await deleteFromDb(dbNames);
                reportFailures('Deleted from the database', r.deleted, r.failures);
            }
            setSelected(new Set());
        } finally {
            setBusy(false);
        }
    };

    const runMove = async (targetSampleId: string) => {
        const result = await moveImages({
            imageIds: movable.map((a) => a.imageId!),
            sampleId: targetSampleId,
        });
        setMoving(false);
        setSelected(new Set());
        if (result.failures.length > 0) {
            window.alert(
                `Moved ${result.moved}/${result.moved + result.failures.length}. ` +
                    `Failures:\n${result.failures.map((f) => `${f.imageId}: ${f.error}`).join('\n')}`,
            );
        }
    };

    return (
        <BackofficePage
            title="Images"
            description={`${projectName} › ${sampleName}${scannerSerial ? ` (scanner ${scannerSerial})` : ''} — acquisitions`}
            actions={
                <Button
                    variant="ghost"
                    onClick={onBack}
                    className="text-[#a8b0bb] hover:bg-[#1e242a]"
                >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Back to samples
                </Button>
            }
        >
            {isLoading ? (
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            ) : error ? (
                <div className="text-red-400 text-sm">{error.toString()}</div>
            ) : (
                <>
                    {emptyCount > 0 && (
                        <div className="mb-3 px-4 py-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-xs text-yellow-200">
                            {emptyCount} acquisition(s) highlighted in yellow:
                            their jp2 and their thumbnail are both 0 byte.
                        </div>
                    )}
                    <DataCard>
                        <DataTable>
                            <thead className="bg-[#0f141a]">
                                <tr>
                                    <TH>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={(el) => {
                                                    if (el)
                                                        el.indeterminate =
                                                            someSelected;
                                                }}
                                                disabled={all.length === 0}
                                                onChange={() =>
                                                    setSelected(
                                                        allSelected
                                                            ? new Set()
                                                            : new Set(all),
                                                    )
                                                }
                                                className="accent-[#74c69d]"
                                                title="Select all"
                                            />
                                            <DropdownMenu>
                                                <DropdownMenuTrigger
                                                    disabled={
                                                        selected.size === 0 ||
                                                        busy
                                                    }
                                                    className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 normal-case tracking-normal text-[11px] text-[#a8b0bb] hover:bg-[#1e242a] hover:text-white disabled:opacity-40 disabled:pointer-events-none"
                                                >
                                                    {busy
                                                        ? 'Working…'
                                                        : selected.size > 0
                                                          ? `${selected.size} selected`
                                                          : 'Actions'}
                                                    <ChevronDownIcon className="h-3 w-3" />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="start"
                                                    className="bg-[#161c22] border-[#1e242a] text-[#d8dde2]"
                                                >
                                                    <MenuItem
                                                        disabled={
                                                            movable.length === 0
                                                        }
                                                        onSelect={() =>
                                                            setMoving(true)
                                                        }
                                                    >
                                                        Move to…
                                                    </MenuItem>
                                                    <DropdownMenuSeparator className="bg-[#1e242a]" />
                                                    <MenuItem
                                                        danger
                                                        disabled={
                                                            inDb.length === 0 &&
                                                            inS3.length === 0
                                                        }
                                                        onSelect={() =>
                                                            runDelete(true, true)
                                                        }
                                                    >
                                                        Delete
                                                    </MenuItem>
                                                    <MenuItem
                                                        danger
                                                        disabled={
                                                            inDb.length === 0
                                                        }
                                                        onSelect={() =>
                                                            runDelete(true, false)
                                                        }
                                                    >
                                                        Delete from DB only
                                                    </MenuItem>
                                                    <MenuItem
                                                        danger
                                                        disabled={
                                                            inS3.length === 0
                                                        }
                                                        onSelect={() =>
                                                            runDelete(false, true)
                                                        }
                                                    >
                                                        Delete from S3 only
                                                    </MenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </TH>
                                    <TH>Acquisition date</TH>
                                    <TH>Files</TH>
                                    <TH align="center">In database</TH>
                                    <TH align="center">In bucket</TH>
                                </tr>
                            </thead>
                            <tbody>
                                {acquisitions?.map((acq) => {
                                    const isEmptyAcquisition =
                                        isEmptyFile(acq.jp2) &&
                                        isEmptyFile(acq.thumb);
                                    return (
                                        <TR
                                            key={acq.jp2Name}
                                            className={
                                                isEmptyAcquisition
                                                    ? 'bg-yellow-500/10 hover:bg-yellow-500/20'
                                                    : ''
                                            }
                                        >
                                            <TD>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(
                                                        acq.jp2Name,
                                                    )}
                                                    onChange={() =>
                                                        toggle(acq.jp2Name)
                                                    }
                                                    className="accent-[#74c69d]"
                                                />
                                            </TD>
                                            <TD>
                                                <EditableAcquisitionDate
                                                    imageId={acq.imageId}
                                                    acquisitionDate={
                                                        acq.acquisitionDate
                                                    }
                                                    onSave={(iso) =>
                                                        updateAcquisitionDate({
                                                            imageId:
                                                                acq.imageId!,
                                                            acquisitionDate: iso,
                                                        })
                                                    }
                                                />
                                            </TD>
                                            <TD>
                                                <div className="flex flex-col gap-1">
                                                    <FileLine
                                                        file={acq.jp2}
                                                        label="jp2"
                                                    />
                                                    <FileLine
                                                        file={acq.thumb}
                                                        label="thumb"
                                                    />
                                                    <FileLine
                                                        file={acq.json}
                                                        label="json"
                                                    />
                                                </div>
                                            </TD>
                                            <TD align="center">
                                                {acq.inDatabase ? (
                                                    <CheckIcon className="h-3.5 w-3.5 inline text-[#74c69d]" />
                                                ) : (
                                                    <span className="text-[#6b7480] text-xs">
                                                        —
                                                    </span>
                                                )}
                                            </TD>
                                            <TD align="center">
                                                {acq.filesInS3 > 0 ? (
                                                    <span className="text-[#a8b0bb] text-xs font-mono">
                                                        {acq.filesInS3}/3
                                                    </span>
                                                ) : (
                                                    <span className="text-[#6b7480] text-xs">
                                                        —
                                                    </span>
                                                )}
                                            </TD>
                                        </TR>
                                    );
                                })}
                            </tbody>
                        </DataTable>
                    </DataCard>
                    <MoveDialog
                        open={moving}
                        onOpenChange={setMoving}
                        projectId={projectId}
                        sampleId={sampleId}
                        count={movable.length}
                        skipped={picked.length - movable.length}
                        onMove={runMove}
                    />
                </>
            )}
        </BackofficePage>
    );
}


function MenuItem({
    children,
    onSelect,
    disabled,
    danger = false,
}: {
    children: React.ReactNode;
    onSelect: () => void;
    disabled?: boolean;
    danger?: boolean;
}) {
    return (
        <DropdownMenuItem
            disabled={disabled}
            onSelect={onSelect}
            className={`text-xs hover:bg-[#1e242a] focus:bg-[#1e242a] ${
                danger ? 'text-red-300 focus:text-red-200' : 'focus:text-white'
            }`}
        >
            {children}
        </DropdownMenuItem>
    );
}

/** Where to move the selection: any sample of any project, this project
 *  first. Only database-tracked acquisitions move. */
function MoveDialog({
    open,
    onOpenChange,
    projectId,
    sampleId,
    count,
    skipped,
    onMove,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: string;
    sampleId: string;
    count: number;
    skipped: number;
    onMove: (targetSampleId: string) => Promise<void>;
}) {
    const { data: projects } = useIntegrityProjects();
    const [targetProjectId, setTargetProjectId] = useState(projectId);
    const [targetSampleId, setTargetSampleId] = useState('');
    const [pending, setPending] = useState(false);
    const { data: targetSamples } = useIntegritySamples(targetProjectId);
    const others = (targetSamples ?? []).filter(
        (s) => targetProjectId !== projectId || s.sampleId !== sampleId,
    );

    useEffect(() => {
        if (!open) return;
        setTargetProjectId(projectId);
        setTargetSampleId('');
    }, [open, projectId]);

    const move = async () => {
        if (!targetSampleId) return;
        setPending(true);
        try {
            await onMove(targetSampleId);
        } finally {
            setPending(false);
        }
    };

    const SELECT =
        'w-full h-10 rounded-md border px-3 text-sm bg-[#0f141a] border-[#1e242a] text-[#e5e7e3] focus:outline-none focus:border-[#2d6a4f] disabled:opacity-40';
    const LABEL =
        'block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                <DialogHeader>
                    <DialogTitle className="text-white">
                        Move {count} acquisition(s)
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className={LABEL}>Project</label>
                        <select
                            value={targetProjectId}
                            onChange={(e) => {
                                setTargetProjectId(e.target.value);
                                setTargetSampleId('');
                            }}
                            className={SELECT}
                        >
                            {projects?.map((p) => (
                                <option key={p.projectId} value={p.projectId}>
                                    {p.projectName}
                                    {p.projectId === projectId
                                        ? ' (current)'
                                        : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={LABEL}>Sample</label>
                        <select
                            value={targetSampleId}
                            onChange={(e) => setTargetSampleId(e.target.value)}
                            disabled={others.length === 0}
                            className={SELECT}
                        >
                            <option value="" disabled>
                                {others.length === 0
                                    ? '— no other sample —'
                                    : '—'}
                            </option>
                            {others.map((s) => (
                                <option key={s.sampleId} value={s.sampleId}>
                                    {s.sampleName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <p className="text-xs text-[#6b7480]">
                        Their files in S3 are renamed to the new sample.
                        {skipped > 0 &&
                            ` ${skipped} selected acquisition(s) not in the database stay where they are.`}
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            className="text-[#a8b0bb] hover:bg-[#1e242a] hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={move}
                            disabled={!targetSampleId || pending}
                            className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                        >
                            {pending ? 'Moving…' : 'Move'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * A file uploaded as an empty object: present in the bucket but 0 byte. A hub
 * that dies mid-upload leaves those behind, and an acquisition whose jp2 *and*
 * thumbnail are both empty carries no image at all — the Images table flags
 * those rows in yellow.
 */
function isEmptyFile(file?: IntegrityAcquisitionFile): boolean {
    return !!file && file.inS3 && file.size === 0;
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1,
    );
    const value = bytes / 1024 ** i;
    return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

function formatDate(iso?: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
}

/** ISO string → value for a <input type="datetime-local" step="1"> (local time). */
function toLocalInputValue(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
}

/**
 * Discreet inline editor for an acquisition's date. Reads as plain text with a
 * pencil that only appears on hover; clicking swaps in a datetime picker with
 * save / cancel. Only DB-tracked acquisitions (those with an imageId) are
 * editable — the date is part of the S3 key, so saving renames the files.
 */
function EditableAcquisitionDate({
    imageId,
    acquisitionDate,
    onSave,
}: {
    imageId?: string;
    acquisitionDate?: string;
    onSave: (iso: string) => Promise<unknown>;
}) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);

    if (!imageId) {
        return <span className="text-[#d8dde2]">{formatDate(acquisitionDate)}</span>;
    }

    const startEditing = () => {
        setValue(toLocalInputValue(acquisitionDate));
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setSaving(false);
    };

    const save = async () => {
        if (!value) return;
        const iso = new Date(value).toISOString();
        if (iso === acquisitionDate) {
            cancel();
            return;
        }
        setSaving(true);
        try {
            await onSave(iso);
            setEditing(false);
        } catch {
            // mutation logs the error; keep the editor open so it can be retried
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1.5">
                <input
                    type="datetime-local"
                    step="1"
                    value={value}
                    autoFocus
                    disabled={saving}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') save();
                        if (e.key === 'Escape') cancel();
                    }}
                    className="bg-[#0a0e12] border border-[#1e242a] rounded px-2 py-1 text-xs text-[#d8dde2]"
                />
                <button
                    onClick={save}
                    disabled={saving}
                    title="Save"
                    className="text-[#74c69d] hover:text-[#9fe0bd] disabled:opacity-40"
                >
                    <CheckIcon className="h-4 w-4" />
                </button>
                <button
                    onClick={cancel}
                    disabled={saving}
                    title="Cancel"
                    className="text-[#a8b0bb] hover:text-[#d8dde2] disabled:opacity-40"
                >
                    <XIcon className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <button
            onClick={startEditing}
            title="Edit acquisition date"
            className="group flex items-center gap-1.5 text-left text-[#d8dde2] hover:text-white"
        >
            <span>{formatDate(acquisitionDate)}</span>
            <PencilIcon className="h-3 w-3 text-[#6b7480] opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
    );
}

function FileLine({
    file,
    label,
}: {
    file?: IntegrityAcquisitionFile;
    label: string;
}) {
    if (!file) {
        return (
            <span className="text-[#6b7480] text-xs font-mono">
                [{label}] —
            </span>
        );
    }
    const baseName = file.name.split('/').pop() ?? file.name;
    const empty = isEmptyFile(file);
    return (
        <span
            className={`text-xs font-mono truncate block max-w-[600px] ${
                empty
                    ? 'text-yellow-300'
                    : file.inS3
                      ? 'text-[#d8dde2]'
                      : 'text-[#6b7480]'
            }`}
            title={file.name}
        >
            [{label}] {baseName}
            {!file.inS3 && (
                <span className="text-[#6b7480] ml-2">(missing)</span>
            )}
            {file.inS3 && file.size !== undefined && (
                <span
                    className={`ml-2 ${empty ? 'text-yellow-300' : 'text-[#6b7480]'}`}
                >
                    ({formatBytes(file.size)})
                </span>
            )}
        </span>
    );
}
